import { filterByHealth } from "./filter_health"
import { HealthFilterPanel, CheckboxFilterPanel, FilterTag, HealthFilterTag } from "./filter_panels"
import { capitalize } from "~/lib/text-helpers"
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "~/react/ui/dropdown-menu"
import { Button } from "~/react/ui/button"
import { Icon } from "~/react/ui/icon"
import { useState, useEffect, useRef } from "react"

export type EmailAccountFilters = {
  status: string[]
  healthFrom: number | null
  healthTo: number | null
  healthIsEmpty: boolean
  healthHasAnyValue?: boolean
  ownerIds: number[]
  searchQuery?: string
}

export type EmailAccount = {
  id: number | null
  email: string
  status?: "active" | "disconnected" | "pending" | "loading"
  sent: number
  limit: number
  scheduled: number
  health: number | null
  health_check_status?: "pending" | "completed" | null
  protection: "warming_up" | "inactive" | null
  owner_id: number
  owner_name: string
  provider: string
  owner_avatar_url?: string
  default_account?: boolean
  managed?: boolean
  display_name?: string
  daily_messages_limit?: number
  signature?: string
  connected_app_id?: number
  managed_mailbox?: {
    first_name?: string
    last_name?: string
    signature?: string
    created_at?: string
    cancel_at?: string
    renewal_date?: string
  }
  _bulkEdit?: {
    accountIds: number[]
    canEditSenderName: boolean
    canEditSignature: boolean
    canEditManagedFields: boolean
  }
}

export type EmailAccountsData = {
  connected: EmailAccount[]
  managed: EmailAccount[]
  total: number
}

export type TeamMember = {
  id: number
  name: string
}

type FilterPanel = "status" | "health" | "owner" | null

const defaultFilters: EmailAccountFilters = {
  status: [],
  healthFrom: null,
  healthTo: null,
  healthIsEmpty: false,
  healthHasAnyValue: false,
  ownerIds: [],
}

export const clearFilters = (): EmailAccountFilters => defaultFilters

export const filterEmailAccounts = (
  data: EmailAccountsData | null,
  filters: EmailAccountFilters,
): EmailAccountsData | null => {
  if (!data) return null

  let filteredConnected = [...(data.connected ?? [])]
  let filteredManaged = [...(data.managed ?? [])]

  // Filter by status (account.status may be undefined until async status fetch completes)
  if (filters.status.length > 0) {
    const filterByStatus = (accounts: EmailAccount[]) =>
      accounts.filter((account) => account.status != null && filters.status.includes(account.status))
    filteredConnected = filterByStatus(filteredConnected)
    filteredManaged = filterByStatus(filteredManaged)
  }

  // Filter by health
  const healthOpts = {
    from: filters.healthFrom,
    to: filters.healthTo,
    isEmpty: filters.healthIsEmpty,
    hasAnyValue: filters.healthHasAnyValue,
  }
  filteredConnected = filterByHealth(filteredConnected, healthOpts)
  filteredManaged = filterByHealth(filteredManaged, healthOpts)

  // Filter by owner
  if (filters.ownerIds.length > 0) {
    const filterByOwner = (accounts: EmailAccount[]) =>
      accounts.filter((account) => filters.ownerIds.includes(account.owner_id))
    filteredConnected = filterByOwner(filteredConnected)
    filteredManaged = filterByOwner(filteredManaged)
  }

  // Filter by search query
  if (filters.searchQuery?.trim()) {
    const query = filters.searchQuery.trim().toLowerCase()
    const filterBySearch = (accounts: EmailAccount[]) =>
      accounts.filter(
        (account) =>
          (account.email?.toLowerCase().includes(query) ?? false) ||
          (account.owner_name?.toLowerCase().includes(query) ?? false) ||
          (account.display_name?.toLowerCase().includes(query) ?? false),
      )
    filteredConnected = filterBySearch(filteredConnected)
    filteredManaged = filterBySearch(filteredManaged)
  }

  return {
    connected: filteredConnected,
    managed: filteredManaged,
    total: filteredConnected.length + filteredManaged.length,
  }
}

type FiltersProps = {
  filters: EmailAccountFilters
  setFilters: (filters: EmailAccountFilters) => void
  teamMembers?: TeamMember[]
  sharedWorkspace?: boolean
}

const STATUS_OPTIONS = ["active", "disconnected", "pending"] as const

const getStatusLabel = (status: string): string => {
  const labels: Record<string, string> = {
    active: i18n.t("js.email_accounts_center.status.active"),
    disconnected: i18n.t("js.email_accounts_center.status.disconnected"),
    pending: i18n.t("js.email_accounts_center.status.pending"),
  }
  return labels[status] || status
}

export const FiltersDropdown = ({ filters, setFilters, teamMembers = [], sharedWorkspace = false }: FiltersProps) => {
  const [activePanel, setActivePanel] = useState<FilterPanel>(null)
  const [tempFilters, setTempFilters] = useState<EmailAccountFilters>(filters)
  const triggerRef = useRef<HTMLButtonElement>(null)

  // Sync tempFilters when filters change externally
  useEffect(() => {
    setTempFilters(filters)
  }, [filters])

  const closeDropdown = () => {
    if (triggerRef.current && (window as any).bootstrap) {
      const dropdownInstance = (window as any).bootstrap.Dropdown.getInstance(triggerRef.current)
      dropdownInstance?.hide()
    }
  }

  const applyFilters = () => {
    setFilters(tempFilters)
    setActivePanel(null)
    closeDropdown()
  }

  const openPanel = (panel: NonNullable<FilterPanel>) => {
    setActivePanel(panel)
  }

  const closePanel = () => {
    setActivePanel(null)
  }

  const renderStatusPanel = () => (
    <CheckboxFilterPanel
      title={i18n.t("js.email_accounts_center.columns.status")}
      options={STATUS_OPTIONS.map((s) => ({
        id: s,
        label: getStatusLabel(s),
        checked: tempFilters.status.includes(s),
      }))}
      onToggle={(id, checked) =>
        setTempFilters({
          ...tempFilters,
          status: checked ? [...tempFilters.status, id as string] : tempFilters.status.filter((s) => s !== id),
        })
      }
      onBack={closePanel}
      onApply={applyFilters}
      idPrefix="filter-status"
    />
  )

  const renderHealthPanel = () => (
    <HealthFilterPanel
      tempFilters={tempFilters}
      setTempFilters={(h) => setTempFilters({ ...tempFilters, ...h })}
      onBack={closePanel}
      onApply={applyFilters}
      titleKey="js.email_accounts_center.columns.health"
    />
  )

  const renderOwnerPanel = () => (
    <CheckboxFilterPanel
      title={i18n.t("js.email_accounts_center.columns.owner")}
      options={teamMembers.map((m) => ({
        id: m.id,
        label: m.name,
        checked: tempFilters.ownerIds.includes(m.id),
      }))}
      onToggle={(id, checked) =>
        setTempFilters({
          ...tempFilters,
          ownerIds: checked ? [...tempFilters.ownerIds, id as number] : tempFilters.ownerIds.filter((i) => i !== id),
        })
      }
      onBack={closePanel}
      onApply={applyFilters}
      idPrefix="filter-owner"
    />
  )

  const filterOptions: {
    panel: NonNullable<FilterPanel>
    label: string
    disabled: boolean
  }[] = [
    {
      panel: "status",
      label: i18n.t("js.email_accounts_center.columns.status"),
      disabled: filters.status.length > 0,
    },
    {
      panel: "health",
      label: i18n.t("js.email_accounts_center.columns.health"),
      disabled:
        filters.healthFrom !== null ||
        filters.healthTo !== null ||
        filters.healthIsEmpty ||
        !!filters.healthHasAnyValue,
    },
    ...(sharedWorkspace && teamMembers.length > 0
      ? [
          {
            panel: "owner" as const,
            label: i18n.t("js.email_accounts_center.columns.owner"),
            disabled: filters.ownerIds.length > 0,
          },
        ]
      : []),
  ]

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button size="sm" ref={triggerRef}>
          <Icon icon="filter" />
          {capitalize(i18n.t("js.filters.label"))}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        {activePanel === null ? (
          <div id="email-accounts-filter-options">
            <ul>
              {filterOptions.map(({ panel, label, disabled }) => (
                <DropdownMenuItem key={panel} disabled={disabled} onClick={() => openPanel(panel)}>
                  {label}
                </DropdownMenuItem>
              ))}
            </ul>
          </div>
        ) : (
          <>
            {activePanel === "status" && renderStatusPanel()}
            {activePanel === "health" && renderHealthPanel()}
            {activePanel === "owner" && teamMembers.length > 0 && renderOwnerPanel()}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export const FiltersTags = ({ filters, setFilters, teamMembers = [], sharedWorkspace = false }: FiltersProps) => {
  const [activeTagPanel, setActiveTagPanel] = useState<FilterPanel>(null)
  const [tempFilters, setTempFilters] = useState<EmailAccountFilters>(filters)

  // Sync tempFilters when filters change externally
  useEffect(() => {
    setTempFilters(filters)
  }, [filters])

  const openTagDropdown = (panel: NonNullable<FilterPanel>) => {
    if (activeTagPanel !== panel) {
      setTempFilters(filters)
    }
    setActiveTagPanel(activeTagPanel === panel ? null : panel)
  }

  const closeTagDropdown = () => {
    setActiveTagPanel(null)
  }

  const isTagDropdownOpen = (panel: NonNullable<FilterPanel>) => activeTagPanel === panel

  const applyFilters = () => {
    setFilters(tempFilters)
    closeTagDropdown()
  }

  const hasFilters =
    filters.status.length > 0 ||
    filters.healthFrom !== null ||
    filters.healthTo !== null ||
    filters.healthIsEmpty ||
    !!filters.healthHasAnyValue ||
    (sharedWorkspace && filters.ownerIds.length > 0)

  if (!hasFilters) return null

  const renderStatusPanel = () => (
    <CheckboxFilterPanel
      title={i18n.t("js.email_accounts_center.columns.status")}
      options={STATUS_OPTIONS.map((s) => ({
        id: s,
        label: getStatusLabel(s),
        checked: tempFilters.status.includes(s),
      }))}
      onToggle={(id, checked) =>
        setTempFilters({
          ...tempFilters,
          status: checked ? [...tempFilters.status, id as string] : tempFilters.status.filter((s) => s !== id),
        })
      }
      onBack={() => closeTagDropdown()}
      onApply={applyFilters}
      idPrefix="filter-status-main"
      hideHeader
    />
  )

  const renderHealthPanel = () => (
    <HealthFilterPanel
      tempFilters={tempFilters}
      setTempFilters={(h) => setTempFilters({ ...tempFilters, ...h })}
      onBack={() => closeTagDropdown()}
      onApply={applyFilters}
      idSuffix="main"
      titleKey="js.email_accounts_center.columns.health"
      hideHeader
    />
  )

  const renderOwnerPanel = () => (
    <CheckboxFilterPanel
      title={i18n.t("js.email_accounts_center.columns.owner")}
      options={teamMembers.map((m) => ({
        id: m.id,
        label: m.name,
        checked: tempFilters.ownerIds.includes(m.id),
      }))}
      onToggle={(id, checked) =>
        setTempFilters({
          ...tempFilters,
          ownerIds: checked ? [...tempFilters.ownerIds, id as number] : tempFilters.ownerIds.filter((i) => i !== id),
        })
      }
      onBack={() => closeTagDropdown()}
      onApply={applyFilters}
      idPrefix="filter-owner-main"
      hideHeader
    />
  )

  return (
    <div className="main-filters mb-4">
      {/* Status filter */}
      {filters.status.length > 0 && (
        <FilterTag
          label={i18n.t("js.email_accounts_center.columns.status")}
          values={filters.status.map((s) => ({
            id: s,
            label: getStatusLabel(s),
          }))}
          onRemoveValue={(id) =>
            setFilters({
              ...filters,
              status: filters.status.filter((s) => s !== id),
            })
          }
          onClearAll={() => setFilters({ ...filters, status: [] })}
          onOpenDropdown={() => openTagDropdown("status")}
          isDropdownOpen={isTagDropdownOpen("status")}
          renderPanel={renderStatusPanel}
        />
      )}

      {/* Health filter */}
      {(filters.healthFrom !== null ||
        filters.healthTo !== null ||
        filters.healthIsEmpty ||
        !!filters.healthHasAnyValue) && (
        <HealthFilterTag
          filters={filters}
          onClear={() =>
            setFilters({
              ...filters,
              healthFrom: null,
              healthTo: null,
              healthIsEmpty: false,
              healthHasAnyValue: false,
            })
          }
          onOpenDropdown={() => openTagDropdown("health")}
          isDropdownOpen={isTagDropdownOpen("health")}
          renderPanel={renderHealthPanel}
          labelKey="js.email_accounts_center.columns.health"
        />
      )}

      {/* Owner filter */}
      {sharedWorkspace && filters.ownerIds.length > 0 && teamMembers.length > 0 && (
        <FilterTag
          label={i18n.t("js.email_accounts_center.columns.owner")}
          values={filters.ownerIds.map((id) => {
            const member = teamMembers.find((m) => m.id === id)
            return { id, label: member?.name || String(id) }
          })}
          onRemoveValue={(id) =>
            setFilters({
              ...filters,
              ownerIds: filters.ownerIds.filter((i) => i !== id),
            })
          }
          onClearAll={() => setFilters({ ...filters, ownerIds: [] })}
          onOpenDropdown={() => openTagDropdown("owner")}
          isDropdownOpen={isTagDropdownOpen("owner")}
          renderPanel={renderOwnerPanel}
        />
      )}
    </div>
  )
}

// Legacy export for backwards compatibility
export const Filters = ({ filters, setFilters, teamMembers, sharedWorkspace }: FiltersProps) => (
  <>
    <FiltersDropdown
      filters={filters}
      setFilters={setFilters}
      teamMembers={teamMembers}
      sharedWorkspace={sharedWorkspace}
    />
    <FiltersTags
      filters={filters}
      setFilters={setFilters}
      teamMembers={teamMembers}
      sharedWorkspace={sharedWorkspace}
    />
  </>
)
